import { useMemo, useCallback } from 'react';
import { FormDataType } from '@tw/tw-component-library';
import { PaymentWorkflowType } from '../../container/forms/types/api/graph.types';
import {
  EnhancedWorkflowConfig,
  getEnhancedWorkflowConfig,
} from './WorkflowConfiguration';
import { workflowsRegistry } from '../../../utils/workflowRegistry';

/**
 * Centralized workflow manager hook
 */
export const useWorkflowManager = (workflowKey: string) => {
  // Get base workflow configuration
  const baseWorkflow = useMemo(() => {
    return workflowsRegistry[workflowKey] || null;
  }, [workflowKey]);

  // Get enhanced configuration
  const enhancedConfig = useMemo(() => {
    if (!baseWorkflow) return null;
    return getEnhancedWorkflowConfig(workflowKey, baseWorkflow);
  }, [workflowKey, baseWorkflow]);

  // Workflow validation
  const validateWorkflow = useCallback(
    (data: FormDataType): { isValid: boolean; errors: string[] } => {
      if (!enhancedConfig) {
        return { isValid: false, errors: ['Workflow configuration not found'] };
      }

      const errors: string[] = [];

      // Basic validation based on workflow type
      switch (enhancedConfig.workflowType) {
        case PaymentWorkflowType.DubaiPaymentApproval:
          if (!data.paymentTotal) errors.push('Payment total is required');
          if (!data.clientName) errors.push('Client name is required');
          break;

        case PaymentWorkflowType.DubaiPaymentFromOfficeAcc:
          if (!data.paymentType) errors.push('Payment type is required');
          if (!data.amount) errors.push('Amount is required');
          if (!data.date) errors.push('Date is required');
          break;

        case PaymentWorkflowType.DubaiTransferRequisition:
          if (!data.paymentType) errors.push('Payment type is required');
          if (!data.amount) errors.push('Amount is required');
          if (!data.date) errors.push('Date is required');
          break;

        case PaymentWorkflowType.OvertimeApproval:
          if (!data.employee_name) errors.push('Employee name is required');
          break;

        default:
          // Generic validation
          if (!data.id) errors.push('Workflow ID is required');
      }

      return {
        isValid: errors.length === 0,
        errors,
      };
    },
    [enhancedConfig]
  );

  // Auto-save functionality
  const autoSave = useCallback(
    async (data: FormDataType): Promise<boolean> => {
      if (!enhancedConfig?.enableAutoSave) return false;

      try {
        // Implement auto-save logic here
        console.log('Auto-saving workflow data:', data);
        // This would typically call an API endpoint to save draft data
        return true;
      } catch (error) {
        console.error('Auto-save failed:', error);
        return false;
      }
    },
    [enhancedConfig]
  );

  // Progress tracking
  const calculateProgress = useCallback(
    (data: FormDataType): number => {
      if (!enhancedConfig) return 0;

      const requiredFields = getRequiredFieldsForWorkflow(
        enhancedConfig.workflowType
      );
      const completedFields = requiredFields.filter(
        (field) => data[field] !== undefined && data[field] !== ''
      );

      return Math.round((completedFields.length / requiredFields.length) * 100);
    },
    [enhancedConfig]
  );

  // Get required fields for a workflow type
  const getRequiredFieldsForWorkflow = (
    workflowType: PaymentWorkflowType
  ): string[] => {
    switch (workflowType) {
      case PaymentWorkflowType.DubaiPaymentApproval:
        return ['paymentTotal', 'clientName', 'paymentDetails'];

      case PaymentWorkflowType.DubaiPaymentFromOfficeAcc:
        return ['paymentType', 'amount', 'date', 'toWhomPayable'];

      case PaymentWorkflowType.DubaiTransferRequisition:
        return ['paymentType', 'amount', 'date', 'toWhomPayable'];

      case PaymentWorkflowType.OvertimeApproval:
        return ['employee_name', 'job_title', 'department'];

      default:
        return ['id'];
    }
  };

  // Get workflow metadata
  const getWorkflowMetadata = useCallback(() => {
    if (!enhancedConfig) return null;

    return {
      id: enhancedConfig.id,
      name: enhancedConfig.name,
      type: enhancedConfig.workflowType,
      isMultiSection: enhancedConfig.isMultiSection,
      hasForm: enhancedConfig.hasForm,
      enableAutoSave: enhancedConfig.enableAutoSave,
      enableValidation: enhancedConfig.enableValidation,
      enableProgressTracking: enhancedConfig.enableProgressTracking,
    };
  }, [enhancedConfig]);

  return {
    workflow: enhancedConfig,
    validateWorkflow,
    autoSave,
    calculateProgress,
    getWorkflowMetadata,
    isConfigured: !!enhancedConfig,
  };
};

/**
 * Get all available workflows with enhanced configuration
 */
export const getAllEnhancedWorkflows = (): Record<
  string,
  EnhancedWorkflowConfig
> => {
  const enhancedWorkflows: Record<string, EnhancedWorkflowConfig> = {};

  Object.keys(workflowsRegistry).forEach((key) => {
    const baseWorkflow = workflowsRegistry[key];
    if (baseWorkflow) {
      enhancedWorkflows[key] = getEnhancedWorkflowConfig(key, baseWorkflow);
    }
  });

  return enhancedWorkflows;
};

/**
 * Get workflows by office
 */
export const getWorkflowsByOffice = (
  office: string
): EnhancedWorkflowConfig[] => {
  const allWorkflows = getAllEnhancedWorkflows();
  return Object.values(allWorkflows).filter(
    (workflow) => workflow.office === office
  );
};

/**
 * Get workflows by type
 */
export const getWorkflowsByType = (
  workflowType: PaymentWorkflowType
): EnhancedWorkflowConfig[] => {
  const allWorkflows = getAllEnhancedWorkflows();
  return Object.values(allWorkflows).filter(
    (workflow) => workflow.workflowType === workflowType
  );
};
